import { useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Loader,
  Popover,
  ScrollArea,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  IconChevronDown,
  IconChevronUp,
  IconCopy,
  IconSearch,
  IconX,
} from "@tabler/icons";
import { getInitials } from "../../../utils/getInitials";

interface AccountRow {
  id: string;
  name: string;
  count: number;
}

interface AccountsPopoverProps {
  totalCount: number;
  assignedEvidences?: any[];
  suggestedEvidences?: any[];
  /** Set when `assignedEvidences` is a paginated list with more pages left to load. */
  hasMoreEvidences?: boolean;
  isLoadingMoreEvidences?: boolean;
  /** Fetches the next page; omit when the evidence lists passed in are already complete. */
  loadMoreEvidences?: () => Promise<{ isError?: boolean } | void> | void;
  children: React.ReactNode;
}

const copyToClipboard = async (text: string, message: string) => {
  try {
    await navigator.clipboard.writeText(text);
    showNotification({ message, color: "green" });
  } catch (e) {
    showNotification({ message: "Failed to copy", color: "red" });
  }
};

const AccountsPopover = ({
  totalCount,
  assignedEvidences,
  suggestedEvidences,
  hasMoreEvidences = false,
  isLoadingMoreEvidences = false,
  loadMoreEvidences,
  children,
}: AccountsPopoverProps) => {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState("");
  const [sortDesc, setSortDesc] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);

  // Reset on every open so a stalled fetch from a previous session doesn't permanently
  // block retrying (e.g. after the user closes, waits out a transient failure, reopens).
  useEffect(() => {
    if (opened) setHasLoadError(false);
  }, [opened]);

  const fetchInFlightRef = useRef(false);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reuses the evidence lists this page already fetched (useIdeaPage's `evidences`/`matches`,
  // or the Explore panel's bulk-evidence query) instead of issuing separate calls just for
  // this popover. When that source is paginated (loadMoreEvidences set), keep pulling pages
  // while the popover is open so the account list matches the full totalCount rather than
  // just whatever page happened to be loaded already. A failed page is terminal for this
  // open — fetchNextPage resolves (doesn't reject) with isError on failure, and hasMoreEvidences
  // stays true afterwards (it reflects the last successful page), so without hasLoadError this
  // would retry the same failing page forever while the popover stays open.
  useEffect(() => {
    if (
      !opened ||
      !hasMoreEvidences ||
      isLoadingMoreEvidences ||
      hasLoadError ||
      !loadMoreEvidences ||
      fetchInFlightRef.current
    ) {
      return;
    }
    fetchInFlightRef.current = true;
    Promise.resolve(loadMoreEvidences())
      .then((result) => {
        if (isMountedRef.current && result?.isError) {
          setHasLoadError(true);
        }
      })
      .catch(() => {
        if (isMountedRef.current) setHasLoadError(true);
      })
      .finally(() => {
        fetchInFlightRef.current = false;
      });
  }, [opened, hasMoreEvidences, isLoadingMoreEvidences, hasLoadError, loadMoreEvidences]);

  const stillLoadingAllEvidences =
    opened && !hasLoadError && (hasMoreEvidences || isLoadingMoreEvidences);

  const evidences = useMemo(
    () => [...(assignedEvidences || []), ...(suggestedEvidences || [])],
    [assignedEvidences, suggestedEvidences]
  );

  const accounts = useMemo<AccountRow[]>(() => {
    const byId = new Map<string, AccountRow>();
    const tally = (company: any) => {
      const id = company?._id || company?.name;
      if (!id || !company?.name) return;
      const existing = byId.get(id);
      if (existing) {
        existing.count += 1;
      } else {
        byId.set(id, { id, name: company.name, count: 1 });
      }
    };
    evidences.forEach((evidence: any) => {
      if (Array.isArray(evidence?.childEvidences) && evidence.childEvidences.length > 0) {
        evidence.childEvidences.forEach((child: any) => tally(child?.company));
      } else {
        tally(evidence?.company);
      }
    });
    return Array.from(byId.values());
  }, [evidences]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q ? accounts.filter((a) => a.name.toLowerCase().includes(q)) : accounts;
    return [...filtered].sort((a, b) =>
      sortDesc
        ? b.count - a.count || a.name.localeCompare(b.name)
        : a.count - b.count || a.name.localeCompare(b.name)
    );
  }, [accounts, search, sortDesc]);

  const totalEvidence = rows.reduce((sum, r) => sum + r.count, 0);

  const copyNames = () =>
    copyToClipboard(rows.map((r) => r.name).join("\n"), `Copied ${rows.length} account names`);
  const copyTable = () =>
    copyToClipboard(
      `Customer\tEvidence\n${rows.map((r) => `${r.name}\t${r.count}`).join("\n")}`,
      `Copied ${rows.length} rows as table`
    );

  return (
    <Popover
      opened={opened}
      onClose={() => setOpened(false)}
      position="bottom-start"
      width={360}
      shadow="md"
      radius="md"
      withinPortal
    >
      <Tooltip
        label="Click to see the account names behind this number"
        disabled={opened}
        withinPortal
      >
        <Popover.Target>
          <Box
            sx={{
              cursor: "pointer",
              borderRadius: 8,
              padding: "4px 8px",
              margin: "-4px -8px",
              "&:hover": { backgroundColor: "#EFEFFC" },
            }}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setOpened((o) => !o);
            }}
          >
            {children}
          </Box>
        </Popover.Target>
      </Tooltip>
      <Popover.Dropdown p={0}>
        <Flex align="center" justify="space-between" px="14px" pt="11px" pb="9px">
          <Text fz={14} fw={700}>
            Accounts requesting this{" "}
            <Text span fz={14} fw={500} color="dimmed">
              · {totalCount}
            </Text>
          </Text>
          <ActionIcon size="sm" onClick={() => setOpened(false)} aria-label="Close">
            <IconX size={17} />
          </ActionIcon>
        </Flex>
        <Box px="14px" pb="9px">
          <TextInput
            placeholder="Search accounts…"
            icon={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            size="xs"
          />
        </Box>
        <Flex
          align="center"
          justify="space-between"
          px="14px"
          py="6px"
          sx={{
            borderTop: "1px solid #E6E9F2",
            borderBottom: "1px solid #E6E9F2",
            backgroundColor: "#F5F4F4",
          }}
        >
          <Text fz={10.5} fw={700} tt="uppercase" color="dimmed" sx={{ letterSpacing: "0.07em" }}>
            Customer
          </Text>
          <Flex
            align="center"
            gap={5}
            sx={{ cursor: "pointer" }}
            onClick={() => setSortDesc((d) => !d)}
          >
            <Text fz={10.5} fw={700} tt="uppercase" color="dimmed" sx={{ letterSpacing: "0.07em" }}>
              Evidence
            </Text>
            {sortDesc ? <IconChevronDown size={12} /> : <IconChevronUp size={12} />}
          </Flex>
        </Flex>
        <ScrollArea.Autosize mah={280}>
          {rows.length === 0 && stillLoadingAllEvidences ? (
            <Flex align="center" justify="center" gap={8} py="22px">
              <Loader size="xs" />
              <Text color="dimmed" fz={12.5}>
                Loading accounts…
              </Text>
            </Flex>
          ) : rows.length ? (
            rows.map((row) => (
              <Flex
                key={row.id}
                align="center"
                px="14px"
                sx={{
                  height: 34,
                  fontSize: 12.5,
                  borderBottom: "1px solid #F2F3F7",
                  "&:hover": { backgroundColor: "#EFEFFC" },
                  "&:hover .accounts-popover-copy": { opacity: 1 },
                }}
              >
                <Flex align="center" gap={9} sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      flex: "0 0 auto",
                      width: 19,
                      height: 19,
                      borderRadius: 5,
                      backgroundColor: "#EFEFFC",
                      color: "#5C5CEB",
                      fontSize: 9.5,
                      fontWeight: 700,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    {getInitials(row.name)}
                  </Box>
                  <Text
                    fw={600}
                    sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {row.name}
                  </Text>
                </Flex>
                <Text fz={12.5} fw={700} mr={4}>
                  {row.count}
                </Text>
                <ActionIcon
                  className="accounts-popover-copy"
                  size="xs"
                  sx={{ opacity: 0 }}
                  onClick={() => copyToClipboard(row.name, `Copied "${row.name}"`)}
                  aria-label={`Copy ${row.name}`}
                >
                  <IconCopy size={13} />
                </ActionIcon>
              </Flex>
            ))
          ) : (
            <Text align="center" color="dimmed" fz={12.5} py="22px">
              {search ? `No accounts match "${search}".` : "No accounts found."}
            </Text>
          )}
        </ScrollArea.Autosize>
        <Flex
          align="center"
          justify="space-between"
          gap={8}
          px="14px"
          py="10px"
          sx={{ borderTop: "1px solid #E6E9F2" }}
        >
          <Text fz={11} color="dimmed">
            {stillLoadingAllEvidences
              ? "Loading all accounts…"
              : hasLoadError
                ? "Couldn't load all accounts — list may be incomplete"
                : `${totalCount} accounts · ${totalEvidence} evidence`}
          </Text>
          <Flex gap={8}>
            <Button
              size="xs"
              variant="default"
              onClick={copyTable}
              disabled={stillLoadingAllEvidences}
            >
              Copy as table
            </Button>
            <Button
              size="xs"
              color="indigo"
              onClick={copyNames}
              disabled={stillLoadingAllEvidences}
            >
              Copy names
            </Button>
          </Flex>
        </Flex>
      </Popover.Dropdown>
    </Popover>
  );
};

export default AccountsPopover;
